/**
 * Rate Limiting Contracts
 * API contracts for managing API rate limiting with dynamic configuration
 */

// ─────────────────────────────────────────────────────────────
// ENUMS
// ─────────────────────────────────────────────────────────────

export enum RateLimitRole {
  SUPER_ADMIN = 'super_admin',
  ORG_ADMIN = 'org_admin',
  USER = 'user',
  VIEWER = 'viewer',
  ANONYMOUS = 'anonymous',
}

export enum OverrideType {
  MULTIPLY = 'multiply',
  FIXED = 'fixed',
  PERCENTAGE = 'percentage',
}

export enum ViolationSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
}

// ─────────────────────────────────────────────────────────────
// RESPONSE DTOs
// ─────────────────────────────────────────────────────────────

export interface GlobalRateLimit {
  id: string
  role: RateLimitRole
  requests_per_minute: number
  requests_per_hour: number
  requests_per_day: number
  block_duration_minutes: number
  created_at: string
  updated_at: string
}

export interface GlobalRateLimitListResponse {
  items: GlobalRateLimit[]
}

export interface TenantOverride {
  id: string
  tenant_id: string
  tenant_name?: string
  override_type: OverrideType
  value_multiplier?: number
  value_fixed?: number
  value_percentage?: number
  expires_at: string | null
  created_at: string
  updated_at: string
}

export interface TenantOverrideListResponse {
  items: TenantOverride[]
  total: number
}

export interface RateLimitViolation {
  id: string
  tenant_id: string | null
  tenant_name?: string
  user_id: string | null
  user_email?: string
  endpoint: string
  ip_address: string
  detected_at: string
  severity: ViolationSeverity
  request_count: number
  limit: number
  window: string
}

export interface RateLimitViolationListResponse {
  items: RateLimitViolation[]
  total: number
}

// ─────────────────────────────────────────────────────────────
// REQUEST DTOs
// ─────────────────────────────────────────────────────────────

export interface CreateGlobalRateLimitRequest {
  role: RateLimitRole
  requests_per_minute: number
  requests_per_hour: number
  requests_per_day: number
  block_duration_minutes: number
}

export interface UpdateGlobalRateLimitRequest {
  requests_per_minute?: number
  requests_per_hour?: number
  requests_per_day?: number
  block_duration_minutes?: number
}

export interface CreateTenantOverrideRequest {
  tenant_id: string
  override_type: OverrideType
  value_multiplier?: number
  value_fixed?: number
  value_percentage?: number
  expires_at?: string
}

export interface UpdateTenantOverrideRequest {
  override_type?: OverrideType
  value_multiplier?: number
  value_fixed?: number
  value_percentage?: number
  expires_at?: string
}

export interface ViolationsListQuery {
  tenant_id?: string
  severity?: ViolationSeverity
  from_date?: string
  to_date?: string
  limit?: number
  offset?: number
}
